<template>
  <div class="tool-call-block" :class="{ 'tool-call-block--done': done }">
    <v-expansion-panels v-model="panelIndex" flat :style="{ width: panelIndex === 0 ? '100%' : 'fit-content' }">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <ToolCallTitle
            :done="done"
            :has-error="hasError"
            :tool-name="formatKey(chunk.data?.tool_name || '')"
            :has-running-step="hasRunningStep"
          />
          <template #actions>
            <BbBtn
              size="x-small"
              type="icon"
              :icon="panelIndex === 0 ? 'chevron-up' : 'chevron-down'"
              icon-size="small"
              no-background
            />
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ToolStepRow
            v-for="(event, i) in events"
            :key="i"
            :event="event"
            :is-loading="getLoadingEventValue(event, i)"
            :is-connected="i < events.length - 1"
            :format-content="(content) => formatStepContent(content, event, i)"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';

import { TOOL_CALL_TYPE } from '~/constants/conversationModels';

import { formatIdleStepLabel } from '~/utils/step-label';
import { formatKey } from '~/utils/format';

import ToolCallTitle from './ToolCallTitle.vue';
import ToolStepRow from './ToolStepRow.vue';

const stripTrailingBr = (html) => html.replace(/(<br\s*\/?>)\s*$/gi, '').trimEnd();

export default {
  name: 'ToolCallBlockSSE',
  components: { ToolCallTitle, ToolStepRow },
  props: {
    chunk: { type: Object, required: true },
    done: { type: Boolean, default: false },
    events: { type: Array, default: () => [] },
    interrupt: { type: Object, default: null },
    sanitizeAndParse: { type: Function, required: true },
    isTyping: { type: Boolean, default: false },
  },
  setup(props) {
    const panelIndex = ref(null);

    const hasError = computed(() => props.events.some((e) => e?.data?.event === TOOL_CALL_TYPE.TOOL_ERROR));

    const activeLoadingIndex = computed(() =>
      props.events.reduce((runningIndex, event, index) => {
        const eventType = event?.data?.event;
        if (eventType === TOOL_CALL_TYPE.TOOL_ERROR) return -1;
        if (!eventType || eventType === TOOL_CALL_TYPE.TOOL_FINISHED) return runningIndex;
        return index;
      }, -1),
    );

    const hasRunningStep = computed(() => !props.done && activeLoadingIndex.value !== -1);

    const getLoadingEventValue = (event, index) => {
      if (!event?.data) {
        return false;
      }

      return hasRunningStep.value && index === activeLoadingIndex.value;
    };

    const formatStepContent = (content, event, index) => {
      const parsed = stripTrailingBr(props.sanitizeAndParse(content));
      return getLoadingEventValue(event, index) ? parsed : formatIdleStepLabel(parsed);
    };

    watch(
      () => props.done,
      (newVal) => {
        panelIndex.value = newVal ? null : 0;
      },
      { immediate: true },
    );

    watch(hasError, (newVal) => {
      if (newVal) panelIndex.value = null;
    });

    return { panelIndex, getLoadingEventValue, hasRunningStep, hasError, formatStepContent, formatKey };
  },
};
</script>

<style scoped lang="scss">
.tool-call-block {
  --tool-step-gap: 12px;

  &--done {
    :deep(.v-expansion-panel) {
      background-color: transparent !important;
      padding: 0 !important;

      .v-expansion-panel-title {
        background-color: $bb-neutral-6 !important;
        border-radius: 100px;
        padding: $bb-spacing-2 !important;
        width: fit-content;
        color: $bb-neutral-0 !important;
        cursor: pointer;

        &:hover,
        &:active,
        &.v-expansion-panel-title--active {
          background-color: rgba(98, 34, 227, 0.2) !important;
          color: #6222e3 !important;
          span,
          .v-expansion-panel-title__icon {
            color: #6222e3 !important;
          }
        }
      }
    }
    :deep(.v-expansion-panel--active .v-expansion-panel-text .v-expansion-panel-text__wrapper) {
      border: 1px solid $bb-neutral-4;
      border-radius: 12px;
      margin-top: $bb-spacing-2;
      padding: $bb-spacing-6 !important;
    }
  }

  &:not(.tool-call-block--done) {
    :deep(.v-expansion-panel-title:not(:hover):not(:active):not(.v-expansion-panel-title--active)) {
      span:not(.tool-step-title--running),
      i {
        color: $bb-neutral-1 !important;
        font-weight: 500 !important;
      }
    }
    :deep(.v-expansion-panel-title) {
      padding: $bb-spacing-2;
      &.v-expansion-panel-title--active,
      &:hover,
      &:active {
        background-color: $bb-neutral-6 !important;
        span,
        i {
          color: inherit;
        }
        svg {
          color: inherit !important;
          fill: inherit !important;
        }
      }
    }
  }

  :deep(.v-expansion-panel) {
    background-color: $bb-neutral-6;
    border-radius: 100px;
    padding: $bb-spacing-2;

    &:not(.v-expansion-panel--active) {
      padding: 0 !important;
      .v-expansion-panel-title {
        height: 34px !important;
        min-height: 34px !important;
      }
    }
    &.v-expansion-panel--active {
      border-radius: 16px !important;
    }

    .v-expansion-panel__shadow {
      box-shadow: none;
    }

    .v-expansion-panel-title {
      min-height: $bb-spacing-8;
      .v-expansion-panel-title__overlay {
        background-color: $bb-neutral-6;
      }
    }
    .v-expansion-panel-title__icon .bb-btn.icon:hover {
      background-color: transparent !important;
    }

    .v-expansion-panel-text .v-expansion-panel-text__wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--tool-step-gap);
      max-height: 20vh;
      overflow: scroll;
      padding-left: 0;
    }
  }
}
</style>
